import { Button, Flex, Stack, Text, UnstyledButton, Box } from "@mantine/core"
import { AppModal } from "@components/ui/AppModal/AppModal"
import AgencyCard from "../../components/AgencyCard"
import type { AgencyAffiliation } from "../AgencyAffiliationsTypes"
import modalStyles from "./Modal.module.css"
import { AEMSvg } from "@components/AEMSvg"
import { useAEMIcons } from "@AEM/hooks/useAEMIcons"
import {
	useAEMDictionary,
	AEM_DICTIONARY_COLLECTIONS
} from "@AEM/hooks/useAEMDictionary"
import { useIsMobile } from "@utils/device.utils"

interface MakeActiveAgencyModalProps {
	opened: boolean
	onClose: () => void
	variant: "active" | "default"
	currentAgency: AgencyAffiliation
	newAgency: AgencyAffiliation
	onConfirm: () => void
	description?: string
	isActivating?: boolean
}

export const MakeActiveAgencyModal = ({
	opened,
	onClose,
	variant,
	currentAgency,
	newAgency,
	onConfirm,
	description,
	isActivating = false
}: MakeActiveAgencyModalProps) => {
	const { dictionary } = useAEMDictionary(
		AEM_DICTIONARY_COLLECTIONS.REGISTRATION
	)
	const isMobile = useIsMobile()
	const { icons } = useAEMIcons()
	const VARIANT_TEXT = {
		active: {
			title: dictionary?.maChangeCurrentAgencyHeader,
			description: dictionary?.maChangeActiveAgencyDescription,
			currentLabel: dictionary?.maCurrentActiveAgencyText,
			newLabel: dictionary?.maChangeActiveAgencyDailogText
		},
		default: {
			title: dictionary?.maChangeDefaultAgencyHeader,
			description: dictionary?.maChangeDefaultAgencyDescription,
			currentLabel: dictionary?.maCurrentDefaultAgencyText,
			newLabel: dictionary?.maChangeDefaultAgencyDailogText
		}
	}

	const text = VARIANT_TEXT[variant]

	return (
		<AppModal
			opened={opened}
			onClose={onClose}
			title={text.title ?? ""}
			titleSize="20px"
			hasScrollableBody={false}
			size={726}
		>
			<Stack gap={24} align="center" px={isMobile ? 0 : 54}>
				<Text
					fz={18}
					lh={1.8}
					ta="center"
					c="var(--text-color-primary)"
					// py={variant === "active" ? 40 : undefined}
					maw={description ? undefined : 416}
					py={description ? 16 : undefined}
				>
					{description ?? text.description ?? ""}
				</Text>

				{/* Current agency card */}
				<AgencyCard
					agency={currentAgency}
					bannerVariant="neutral"
					bannerLabel={text.currentLabel ?? ""}
					w="100%"
				/>

				<Box
					style={
						{
							"--svg-color": "var(--mantine-color-royal-126)"
						} as React.CSSProperties
					}
				>
					<AEMSvg svgData={icons["switch-icon.svg"]} w={39} h={43} />
				</Box>

				{/* New agency card */}
				<AgencyCard
					agency={newAgency}
					bannerVariant="active"
					bannerLabel={text.newLabel ?? ""}
					w="100%"
				/>

				<Flex
					direction={isMobile ? "column" : "row"}
					justify="center"
					align="center"
					gap={isMobile ? 12 : 100}
					pt={16}
					pb={18}
					w="100%"
				>
					<Button
						className={modalStyles.confirmButton}
						onClick={onConfirm}
						disabled={isActivating}
						w={isMobile ? "100%" : undefined}
						style={{
							order: isMobile ? -1 : 1,
							...(isMobile && { padding: "10px 24px", height: "48px" })
						}}
					>
						{dictionary?.confirmButtonLabel ?? ""}
					</Button>
					<UnstyledButton
						className={modalStyles.cancelButton}
						onClick={onClose}
						disabled={isActivating}
					>
						{dictionary?.cancelButtonLabel ?? ""}
					</UnstyledButton>
				</Flex>
			</Stack>
		</AppModal>
	)
}
